import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  Loader2, CheckCircle2, ChevronLeft, ChevronRight,
  Eraser, MapPin, Truck, PackageCheck, Clock, FileText,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { toast } from '../../../hooks/use-toast';
import AudioInstruction from '../AudioInstruction';
import GuidedPhotoCapture from '../GuidedPhotoCapture';
import EmojiStateSelector, { EMOJI_TO_TEXT } from '../EmojiStateSelector';
import ObservationTemplates from '../ObservationTemplates';
import QuickWeightInput from '../QuickWeightInput';

const STEPS = ['Arrivée', 'Détails', 'Photos', 'Signature'];

const PHOTO_SLOTS_LOADING = [
  { key: 'camion_vide', label: 'Camion vide avant chargement', shortLabel: 'Camion vide',
    audio: 'Photo du camion vide avant chargement', required: true, exampleEmoji: '🚛' },
  { key: 'marchandise', label: 'Marchandise avant mise en camion', shortLabel: 'Marchandise',
    audio: 'Photo de la marchandise avant mise en camion', required: true, exampleEmoji: '📦' },
  { key: 'camion_charge', label: 'Camion chargé, porte ouverte', shortLabel: 'Camion chargé',
    audio: 'Photo du camion chargé porte ouverte', required: true, exampleEmoji: '🚚' },
  { key: 'autre', label: 'Photo additionnelle (optionnelle)', shortLabel: 'Autre',
    audio: 'Photo additionnelle libre', required: false, exampleEmoji: '📷' },
];

const PHOTO_SLOTS_UNLOADING = [
  { key: 'camion_avant_dechargement', label: 'Camion avant déchargement', shortLabel: 'Camion avant',
    audio: 'Photo du camion avant déchargement, porte fermée', required: true, exampleEmoji: '🚛' },
  { key: 'marchandise_recue', label: 'Marchandise reçue sur site', shortLabel: 'Marchandise',
    audio: 'Photo de la marchandise reçue sur site', required: true, exampleEmoji: '📦' },
  { key: 'lieu_livraison', label: 'Lieu de livraison', shortLabel: 'Lieu',
    audio: 'Photo du lieu de livraison', required: true, exampleEmoji: '🏪' },
  { key: 'autre', label: 'Photo additionnelle (optionnelle)', shortLabel: 'Autre',
    audio: 'Photo additionnelle libre', required: false, exampleEmoji: '📷' },
];

/**
 * PM Spec V3 13/05/2026 — DriverFlow
 *
 * 4 écrans optimisés tablette pour le chauffeur :
 *   1. Arrivée → GPS auto + bouton "Je suis arrivé"
 *   2. Détails → poids (boutons rapides) + état emoji + observations cochables
 *   3. Photos → checklist guidée (3 obligatoires + 1 optionnelle)
 *   4. Signature
 */
const DriverFlow = ({
  type, operation, operatorName,
  endpointPrefix, authHeaders, linkId, noteId,
  onCompleted,
}) => {
  const isLoading = type === 'loading';
  // Bug fix 16/02/2026 — Stabilise axios.create via useState lazy init
  const [ax] = useState(() => axios.create({ headers: authHeaders || {} }));
  const [step, setStep] = useState(0);
  const [arrived, setArrived] = useState(false);
  const [arrivalTime, setArrivalTime] = useState('');
  const [startTime, setStartTime] = useState('');
  const [actualWeight, setActualWeight] = useState('');
  const [stateEmoji, setStateEmoji] = useState('good');
  const [observations, setObservations] = useState('');
  const [photos, setPhotos] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [gps, setGps] = useState(null);
  const sigRef = useRef(null);
  const noteRef = useRef(null);

  const photoSlots = isLoading ? PHOTO_SLOTS_LOADING : PHOTO_SLOTS_UNLOADING;

  // Capture GPS au mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, { timeout: 5000 }
    );
  }, []);

  // PM Bug fix 17/02/2026 — Priorité au noteId scopé (JWT) en multi-rotation
  const ensureNote = useCallback(async () => {
    if (noteRef.current) return noteRef.current;
    const list = await ax.get(endpointPrefix);
    const allNotes = list.data || [];
    let existing;
    if (noteId) existing = allNotes.find(n => n.id === noteId);
    if (!existing && linkId) {
      existing = allNotes.find(n =>
        (n.signatureLinks || []).some(l => l.id === linkId)
      );
    }
    if (!existing) existing = allNotes.find(n => n.type === type);
    if (existing) {
      noteRef.current = existing;
      return existing;
    }
    const created = await ax.post(endpointPrefix, { type });
    noteRef.current = created.data;
    return created.data;
  }, [endpointPrefix, type, ax, linkId, noteId]);

  useEffect(() => { ensureNote().catch(() => {}); }, [ensureNote]);

  const onArrived = async () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    setArrivalTime(`${hh}:${mm}`);
    setStartTime(`${hh}:${mm}`);
    setArrived(true);
    // Set GPS arrival on note
    try {
      const note = await ensureNote();
      if (gps) {
        await ax.patch(`${endpointPrefix}/${note.id}/arrival-gps`, gps);
      }
    } catch { /* non-blocking */ }
    setStep(1);
  };

  const submitAll = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast({ title: 'Signature requise', description: 'Signez avant de valider', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const note = await ensureNote();
      const noteId = note.id;
      // 1) Update note details
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const endTime = `${hh}:${mm}`;
      await ax.patch(`${endpointPrefix}/${noteId}`, {
        actualDate: new Date().toISOString().slice(0, 10),
        arrivalTime, startTime, endTime,
        actualWeight: actualWeight ? parseFloat(actualWeight) : undefined,
        actualState: EMOJI_TO_TEXT[stateEmoji] || 'Bon état',
        observations,
      });
      // 2) Add photos
      for (const [key, p] of Object.entries(photos)) {
        if (!p) continue;
        const slot = photoSlots.find(s => s.key === key);
        await ax.post(`${endpointPrefix}/${noteId}/photos`, {
          label: slot?.key || key,
          base64: p.dataUrl,
          fileSize: p.fileSize,
          source: p.source || 'camera_live',
          originalTakenAt: p.originalTakenAt,
          replaceByLabel: true,   // Bug fix 16/02 — idempotence re-tries
        });
      }
      // 3) Sign as driver
      const sigData = sigRef.current.getCanvas().toDataURL('image/png');
      await ax.post(`${endpointPrefix}/${noteId}/sign`, {
        party: 'driver',
        name: operatorName || 'Chauffeur',
        signatureBase64: sigData,
        gpsCoords: gps,
        channel: 'driver_device',
        linkId: linkId || undefined,
      });
      setSuccess(true);
      onCompleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur inconnue';
      toast({ title: 'Échec', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Card className="border-emerald-200 bg-emerald-50" data-testid="driver-success">
        <CardContent className="p-8 text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-white" />
          </div>
          <div className="text-xl font-bold text-emerald-800">Bon signé avec succès !</div>
          <div className="text-sm text-slate-600">Bonne route.</div>
        </CardContent>
      </Card>
    );
  }

  // ─── Header avec mini-stepper ───
  const Header = () => (
    <div className="flex items-center gap-1 mb-4" data-testid="driver-stepper">
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div className={`flex items-center gap-1 ${i === step ? 'text-emerald-700 font-bold' : 'text-slate-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
              i < step ? 'bg-emerald-600 text-white' : i === step ? 'bg-emerald-600 text-white' : 'bg-slate-200'
            }`}>
              {i < step ? '✓' : i + 1}
            </div>
            <span className="text-xs hidden sm:inline">{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
        </React.Fragment>
      ))}
    </div>
  );

  // ─── Step 0 : Arrivée ───
  if (step === 0) {
    const greeting = `Bonjour${operatorName ? ' ' + operatorName : ''}. Vous êtes au lieu de ${isLoading ? 'chargement' : 'déchargement'} ?`;
    return (
      <Card data-testid="driver-step-0">
        <CardContent className="p-5 space-y-5">
          <Header />
          <div className="text-xl font-bold text-slate-900 leading-tight">{greeting}</div>
          <AudioInstruction text={greeting} testId="driver-step0-audio" />
          {gps && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <MapPin className="w-3 h-3 text-emerald-600" /> Position détectée
            </div>
          )}
          <button
            type="button"
            data-testid="driver-arrived-btn"
            onClick={onArrived}
            className="w-full rounded-2xl border-4 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 p-6 flex flex-col items-center justify-center gap-2 min-h-[180px] transition-all active:scale-95"
          >
            {isLoading ? <Truck className="w-14 h-14 text-emerald-600" /> : <PackageCheck className="w-14 h-14 text-emerald-600" />}
            <span className="text-lg font-bold text-emerald-700">OUI, JE SUIS ARRIVÉ</span>
          </button>
        </CardContent>
      </Card>
    );
  }

  // ─── Step 1 : Détails (poids, état, observations) ───
  if (step === 1) {
    return (
      <Card data-testid="driver-step-1">
        <CardContent className="p-5 space-y-5">
          <Header />
          <div>
            <div className="text-base font-semibold text-slate-900">Poids constaté</div>
            <div className="mt-2">
              <QuickWeightInput
                value={actualWeight} onChange={setActualWeight}
                expectedWeight={operation?.weight}
                testId="driver-weight"
              />
            </div>
          </div>
          <div>
            <div className="text-base font-semibold text-slate-900 mb-2">État du {isLoading ? 'chargement' : 'déchargement'}</div>
            <EmojiStateSelector value={stateEmoji} onChange={setStateEmoji} testId="driver-emoji-state" />
          </div>
          <div>
            <div className="text-base font-semibold text-slate-900 mb-2">Observations</div>
            <ObservationTemplates type={type} value={observations} onChange={setObservations} testId="driver-observations" />
          </div>
          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={() => setStep(0)} data-testid="driver-step1-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <Button size="lg" onClick={() => setStep(2)} data-testid="driver-step1-next"
                    className="bg-emerald-600 hover:bg-emerald-700">
              Photos <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Step 2 : Photos guidées ───
  if (step === 2) {
    const requiredFilled = photoSlots.filter(s => s.required).every(s => photos[s.key]);
    return (
      <Card data-testid="driver-step-2">
        <CardContent className="p-5 space-y-4">
          <Header />
          <div>
            <Badge className="bg-emerald-600 text-white border-0">Photos preuves</Badge>
            <div className="text-base font-semibold text-slate-900 mt-2">
              {photoSlots.filter(s => s.required).length} photos obligatoires + 1 libre
            </div>
          </div>
          <GuidedPhotoCapture
            slots={photoSlots}
            value={photos}
            onChange={(key, p) => setPhotos((prev) => {
              const next = { ...prev };
              if (p) next[key] = p; else delete next[key];
              return next;
            })}
            testId="driver-photos"
          />
          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="driver-step2-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <Button size="lg" disabled={!requiredFilled} onClick={() => setStep(3)}
                    data-testid="driver-step2-next"
                    className="bg-emerald-600 hover:bg-emerald-700">
              Signature <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Step 3 : Signature ───
  const controlsDone = !!stateEmoji;
  const photoDone = Object.values(photos).some((p) => p);
  const canSign = controlsDone && photoDone;
  return (
    <Card data-testid="driver-step-3">
      <CardContent className="p-5 space-y-4">
        <Header />
        <div className="text-xl font-bold text-slate-900">Signez ici{operatorName ? `, ${operatorName}` : ''}</div>
        <AudioInstruction text="Signez avec votre doigt sur la zone blanche" testId="driver-step3-audio" />
        <div className="border-2 border-dashed border-emerald-300 rounded-lg bg-white">
          <SignatureCanvas
            ref={sigRef}
            penColor="#0f172a"
            canvasProps={{ className: 'w-full h-56 rounded-lg', 'data-testid': 'driver-signature-canvas' }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => sigRef.current?.clear()}
                  data-testid="driver-clear-sig" className="gap-1.5">
            <Eraser className="w-3.5 h-3.5" /> Effacer
          </Button>
        </div>
        {!canSign && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
               data-testid="driver-sign-requirements">
            <span className="font-semibold">Contrôles et photo requis :</span>
            <span>
              {!controlsDone && '• État de la marchandise (étape Détails) '}
              {!photoDone && '• Au moins une photo de la marchandise'}
            </span>
          </div>
        )}
        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" onClick={() => setStep(2)} data-testid="driver-step3-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <Button size="lg" disabled={submitting || !canSign} onClick={submitAll}
                  data-testid="driver-submit-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2 h-14 px-6 text-base font-bold">
            {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Enregistrement…</>)
                       : (<><CheckCircle2 className="w-5 h-5" /> J'ai signé</>)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DriverFlow;
